import { eventChannel, END } from 'redux-saga';
import { onSnapshot } from 'firebase/firestore';
import { debugErrorMap } from 'firebase/auth';
import { ref, uploadBytes } from 'firebase/storage';
import axios from 'axios';

import { storage } from 'firebaseConfig';

import { upgradePlans, appsSumoTiers } from './contentProvider';
import theme from '../styles/theme';

/**
 * @param {string} string
 * @return {string}
 */

export function camelCaseToDash(string) {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * @param {string} string
 * @return {string}
 */

export function dashToCamelCase(string) {
  return string.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

/**
 * @param {string} rgb
 * @return {string}
 */

export function rgbToHex(rgb) {
  if (!rgb) return '';

  [rgb] = rgb.split('(')[1].split(')');
  rgb = rgb.split(',');
  let hex = rgb.map((x) => {
    x = parseInt(x, 10).toString(16);
    return x.length === 1 ? `0${x}` : x;
  });
  hex = hex.join('');
  return hex;
}

/**
 * A debounce function.
 * @param {function} func
 * @param {number} wait
 * @param {boolean} immediate
 * @return {function} Callback function.
 */

export function debounce(func, wait, immediate) {
  let timeout;

  return () => {
    const context = this;
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

/**
 * Convert relative URLs to absolute.
 * @param {string} base `https://www.example.com/page/`
 * @param {string} relative `/assets/img/image.png`
 * @return {string} `https://www.example.com/assets/img/image.png`
 */

export function relativeToAbsolute(base, relative) {
  // if (!base.endsWith('/')) base = `${base}/`;
  return new URL(relative, base).href;
}

/**
 * Converts a Dayjs relative time string to shorter form.
 * @param {string} time a few days ago
 * @return {string} 1d
 */

export function shortenRelativeTime(time) {
  const lastIndex = time.lastIndexOf(' ');
  const lastWord = time.split(' ').splice(-1)[0];

  time = time.substring(0, lastIndex);

  if (time.startsWith('a few')) time = time.replace('a few', '1');
  if (time.startsWith('an')) time = time.replace('an', '1');
  if (time.startsWith('a')) time = time.replace('a', '1');

  return `${time}${
    lastWord === 'month' || lastWord === 'months' ? 'mo' : lastWord.charAt(0)
  }`;
}

/**
 * Creates and returns a Redux Saga event channel at a Firebase document/collection reference.
 * @param {object} reference
 * @param {function} reference.onSnapshot
 * @return {function} Event channel.
 */

export const createDocumentChangeChannel = (reference) =>
  eventChannel((emitter) =>
    onSnapshot(reference, {
      next: (snapshot) => emitter(snapshot),
      error: (error) => emitter(new Error(error.message)),
      complete: () => emitter(END),
    }),
  );

/**
 * Find and return all font families defined in the entire HTML document.
 * @param {HTMLDocument} d A valid document element.
 * @return {array} Array of font families.
 */

export function listFonts(d) {
  const fonts = [];

  if (d) {
    const nodes = Array.from(d.querySelectorAll('*'));

    nodes.forEach((i) => {
      if (i.style) {
        ['', ':before', ':after'].forEach((j) => {
          const { fontFamily } = getComputedStyle(i, j);

          fonts.push(fontFamily);
        });
      }
    });
  }

  return [...new Set(fonts)];
}

/**
 * @param {string|number} weight
 * @return {number} Font weight.
 */

export function stringToFontWeight(weight) {
  if (Number(weight) % 100 !== 0) {
    if (weight === 'normal') return 400;
    if (weight === 'bold') return 700;
    if (weight === 'bolder') return 800;
    if (weight === 'lighter') return 300;
  }

  return Number(weight);
}

/**
 * Convert string in pixel to number.
 * @param {string|number} value 12px
 * @return {number} 12
 */

export function pxToNumber(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  return Number(value.replace('px', ''));
}

/**
 * Escapes a string to be able to used for RegExp.
 * @param {string} string
 * @return {string}
 */

export function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

/**
 * Returns element offset position relative to the document.
 * @param {HTMLElement} element
 * @param {string} direction
 * @return {number} Offset value.
 */

export const getOffset = (element, direction) => {
  let offset = 0;

  if (!element) return offset;

  const rect = element.getBoundingClientRect();

  offset =
    rect[direction.toLowerCase()] +
    element.ownerDocument.documentElement[
      `scroll${direction.charAt(0)}${direction.substring(1)}`
    ];

  return offset;
};

/**
 * Get query selector string for an HTML element.
 * @param {HTMLElement} el
 * @return {string}
 */

export function getQuerySelector(el) {
  if (el) {
    let str = '';

    if (el.tagName?.toLowerCase() === 'body') return 'body';

    str = el.tagName?.toLowerCase();

    if (el.parentElement && el.parentElement.children.length > 1) {
      if (el.parentElement.querySelectorAll(str).length > 1) {
        const similarNodes = [];
        let nodeNth = '';

        Array.from(el.parentElement.children).forEach((c) => {
          if (el.nodeName === c.nodeName) similarNodes.push(c);
        });

        if (similarNodes.length > 1)
          Array.from(similarNodes).forEach((s, index) => {
            if (el === s) nodeNth = `:nth-of-type(${index + 1})`;
          });

        str += nodeNth;
      }
    }

    return `${getQuerySelector(el.parentNode)} > ${str}`;
  }

  return '';
}

/**
 * Adds workaround for CSS2Properties while using Object.keys.
 */

export function objectKeys(object) {
  switch (object.constructor.name) {
    case 'CSS2Properties':
      return Object.values(object).map((i) => dashToCamelCase(i));
    default:
      return Object.keys(object);
  }
}

// export class Caret {
//   /**
//    * get/set caret position
//    * @param {HTMLColletion} target
//    */
//   constructor(target) {
//     this.isContentEditable = target && target.contentEditable;
//     this.target = target;
//   }

//   /**
//    * get caret position
//    * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Range}
//    * @returns {number}
//    */
//   getPos() {
//     // for contentedit field
//     if (this.isContentEditable) {
//       this.target.focus();
//       const _range = document.getSelection().getRangeAt(0);
//       const range = _range.cloneRange();
//       range.selectNodeContents(this.target);
//       range.setEnd(_range.endContainer, _range.endOffset);
//       return range.toString().length;
//     }
//     // for texterea/input element
//     return this.target.selectionStart;
//   }

//   /**
//    * set caret position
//    * @param {number} pos - caret position
//    */
//   setPos(pos) {
//     // for contentedit field
//     if (this.isContentEditable) {
//       this.target.focus();
//       document.getSelection().collapse(this.target, pos);
//       return;
//     }
//     this.target.setSelectionRange(pos, pos);
//   }
// }

export function placeCaretAtEnd(el) {
  el.focus();
  if (
    typeof window.getSelection !== 'undefined' &&
    typeof document.createRange !== 'undefined'
  ) {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } else if (typeof document.body.createTextRange !== 'undefined') {
    const textRange = document.body.createTextRange();
    textRange.moveToElementText(el);
    textRange.collapse(false);
    textRange.select();
  }
}

export function getURLFromCSSURL(url) {
  return url
    .split('(')[1]
    .replace(')', '')
    .replace(/"/gi, '')
    .replace(/'/gi, '');
}

/**
 * @param {string} u
 */

export function convertDownloadURLToGS(u) {
  let url = u;

  if (url.startsWith("url('https")) url = getURLFromCSSURL(url);
  if (!url.startsWith('https://firebasestorage.googleapis.com')) return '';
  url = new URL(url);
  url = url.pathname.split('/');
  url = url[url.length - 1];
  url = url.replace(/%2F/g, '/');
  url = `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${url}`;

  return url;
}

/**
 * @param {string} u
 */

export function convertGSToDownloadURL(u, timestamp = false) {
  const path = u.replace(
    `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/`,
    '',
  );

  return `https://firebasestorage.googleapis.com/v0/b/${
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET
  }/o/${encodeURIComponent(path)}?alt=media${
    timestamp
      ? `&time=${typeof timestamp === 'string' ? timestamp : Date.now()}`
      : ''
  }`;
}

export function getUserPhotoURL(url, allowAvatar = true) {
  if (!url) return null;
  if (allowAvatar) {
    if (url.startsWith('avatar-')) {
      return `/assets/img/avatars/${url}.svg`;
    }
  }

  if (url.startsWith('gs')) {
    return convertGSToDownloadURL(url, true);
  }

  return url;
}

/**
 * @param {string} sheet
 * @param {string} pageURL
 * @param {string} finalURL
 * @param {string} proxyURL
 * @returns {string}
 */

export function replaceStylesheetUrls(sheet, pageURL, finalURL, proxyURL) {
  let imports = sheet.match(/@import.*?["']([^"']+)["'].*?;/gi);
  const urls = sheet.match(/url\(.*?\)/gi);
  const urlReg = new RegExp(pageURL, 'g', 'i');
  let newSheet = sheet;

  if (imports && imports.length > 0) {
    imports = imports.filter((i) => !i.match(/url\(/gi));

    imports.forEach((u) => {
      let filteredUrl = u.match(/(["'])(?:(?=(\\?))\2.)*?\1/gi, '')[0];

      filteredUrl = filteredUrl.replace(/"/gi, '').replace(/'/gi, '');
      if (filteredUrl.startsWith('data')) return;
      if (!filteredUrl.startsWith('http'))
        filteredUrl = relativeToAbsolute(finalURL, filteredUrl);
      if (filteredUrl.startsWith('/')) filteredUrl = `${pageURL}${filteredUrl}`;
      if (filteredUrl.startsWith(pageURL))
        filteredUrl = filteredUrl.replace(urlReg, proxyURL);

      newSheet = newSheet.replace(u, `@import '${filteredUrl}';`);
    });
  }

  if (urls && urls.length > 0)
    urls.forEach((u) => {
      let filteredUrl = getURLFromCSSURL(u);

      if (filteredUrl.startsWith('data')) return;
      if (!filteredUrl.startsWith('http'))
        filteredUrl = relativeToAbsolute(finalURL, filteredUrl);
      if (filteredUrl.startsWith('/')) filteredUrl = `${pageURL}${filteredUrl}`;
      if (filteredUrl.startsWith(pageURL))
        filteredUrl = filteredUrl.replace(urlReg, proxyURL);

      newSheet = newSheet.replace(u, `url('${filteredUrl}')`);
    });

  return newSheet;
}

export function nullFunction() {
  return null;
}

export const frameOutlineWidth = (frameInstance) =>
  pxToNumber(frameInstance.style.borderWidth);

function getChangeCSS(change) {
  switch (change.changeType) {
    case 'image':
      return `${change.querySelector} { height: ${change.imageDimensions.height} !important; width: ${change.imageDimensions.width} !important; object-fit: cover !important; max-width: 100% !important; }`;
    default:
      switch (change.styleProperty) {
        case 'width':
        case 'height':
          return `${change.querySelector} { ${change.styleProperty}: ${change.styleValue} !important; min-${change.styleProperty}: ${change.styleValue} !important; max-${change.styleProperty}: ${change.styleValue} !important; }`;
        default:
          return `${change.querySelector} { ${change.styleProperty}: ${change.styleValue} !important; }`;
      }
  }
}

export function getChangesStylesheet(changes) {
  let defaultStyles = '';
  let mobileStyles = '';
  let desktopOnlyStyles = '';

  changes.forEach((i) => {
    if (i.deviceSize === 'default') {
      if (i.changeType === 'style' && i.styleProperty === 'width') {
        desktopOnlyStyles = `${desktopOnlyStyles}${i.querySelector} { ${i.styleProperty}: ${i.styleValue} !important; max-${i.styleProperty}: ${i.styleValue} !important; } `;
        mobileStyles = `${mobileStyles}${i.querySelector} { ${i.styleProperty}: ${i.styleValue} !important; max-${i.styleProperty}: 100% !important; } `;
      } else defaultStyles = `${defaultStyles}${getChangeCSS(i)} `;
    } else mobileStyles = `${mobileStyles}${getChangeCSS(i)} `;
  });

  return `${defaultStyles} @media (min-width: 741px) { ${desktopOnlyStyles} } @media (max-width: 740px) { ${mobileStyles} }`;
}

export const getZIndex = (el, highestZ = 0) => {
  const z =
    el?.ownerDocument?.defaultView
      .getComputedStyle(el)
      .getPropertyValue('z-index') || 0;

  if (el && el.nodeName !== 'BODY')
    return getZIndex(
      el.parentNode,
      Number(z) > Number(highestZ) ? z : highestZ,
    );

  return Number(Number(z) > Number(highestZ) ? z : highestZ) + 1;
};

export const checkPositionFixed = (el) => {
  const p =
    el?.ownerDocument?.defaultView
      .getComputedStyle(el)
      .getPropertyValue('position') || 'undefined';

  if (p !== 'fixed' && p !== 'undefined')
    return checkPositionFixed(el.parentNode);

  return p === 'fixed';
};

export const getLoaderStyle = () => `
  .loader {
    position: relative;
    margin: 0 auto;
  }

  .loader::before {
    content: '';
    display: block;
    padding-top: 100%;
  }

  .loader .circular {
    animation: rotate 1.6s linear infinite;
    height: 100%;
    transform-origin: center center;
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }

  .loader .path {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    animation: dash 1.5s ease-in-out infinite;
    stroke-linecap: round;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -35px;
    }
    100% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -124px;
    }
  }
`;

export const getSizeInMB = (size = 1) => 1000000 * size;

export const getInspectChangesSheet = (inspectChanges) => {
  const inspectorStyles = `[data-ruttl-selected="true"] { transition-duration: 0s !important; user-select: none !important; outline: 0 !important; }`;

  if (inspectChanges?.length > 0)
    return `${inspectorStyles} ${getChangesStylesheet(
      /* eslint-disable indent */
      inspectChanges
        .filter(
          (i) =>
            (i.changeType === 'style' || i.changeType === 'image') &&
            i.querySelector &&
            i.styleValue,
        )
        .map((i) =>
          typeof i.styleValue?.startsWith === 'function' &&
          i.styleValue.startsWith('gs://')
            ? {
                ...i,
                styleValue: `url('${convertGSToDownloadURL(i.styleValue)}')`,
              }
            : i,
        ),
      /* eslint-enable indent */
    )}`;

  return inspectorStyles;
};

export const applyTextChanges = (inspectChanges, frameInstance) => {
  if (inspectChanges?.length > 0 && frameInstance?.contentDocument)
    inspectChanges
      .filter((i) => i.changeType === 'text')
      .forEach((i) => {
        const el = frameInstance.contentDocument.querySelector(
          i.querySelector || `[data-rally-element-id="${i.id}"]`,
        );

        if (el && i.textValue) el.innerHTML = i.textValue;
      });
};

export const applyImageChanges = (inspectChanges, frameInstance) => {
  if (inspectChanges?.length > 0 && frameInstance?.contentDocument)
    inspectChanges
      .filter((i) => i.changeType === 'image')
      .forEach((i) => {
        const el = frameInstance.contentDocument.querySelector(
          i.querySelector || `[data-rally-element-id="${i.id}"]`,
        );

        if (el && i.imageSrc) {
          el.setAttribute('data-ruttl-bypass', true);
          el.src = i.imageSrc;
          el.style.setProperty('height', i.imageDimensions.height, 'important');
          el.style.setProperty('width', i.imageDimensions.width, 'important');
          el.removeAttribute('srcset');
          el.removeAttribute('sizes');
          el.removeAttribute('loading');
          if (Object.keys(el.dataset).length > 0)
            Object.keys(el.dataset).forEach((key) => {
              if (key !== 'ruttlBypass') delete el.dataset[key];
            });
        }
      });
};

export const copyTextToClipboard = (text) => {
  let response = false;

  if (!text) response = false;

  const textArea = document.createElement('textarea');

  textArea.value = text;
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  response = document.execCommand('copy');
  document.body.removeChild(textArea);

  return response;
};

export const getPlanDetails = (planID) => {
  if (!planID) return '';

  return upgradePlans.find(({ id }) => {
    if (planID.startsWith('team') && id === 'team') return true;
    return id === planID;
  });
};

export const getPlanTitle = (planID) => {
  if (!planID) return '';

  if (planID?.startsWith('team')) {
    const [plan, count] = planID.split('-');

    return `${plan} (${count} users)`;
  }

  return planID;
};

export const getAppsumoLastInvoice = (invoices) => {
  if (!invoices || !Array.isArray(invoices) || invoices.length === 0)
    return null;

  return invoices.sort(
    (a, b) => b.createdAt.valueOf() - a.createdAt.valueOf(),
  )[0];
};

export const memoizer = (func) => {
  const cache = {};

  return (n) => {
    if (cache[n] !== undefined) return cache[n];

    const result = func(n);

    cache[n] = result;

    return result;
  };
};

export const getImageHTML = (currentPage, projectID, versionID) =>
  `<!DOCTYPE html><html lang="en"><head><style>html, body{margin: 0; padding: 0; background: #e7e7e7;}.image-container{margin: 65px 32px; text-align: center;}.image-container > img{border-radius: 4px; box-shadow: 0 0 0 2px ${
    theme.colors.blue
  }; max-width: 100%; image-rendering: -webkit-optimize-contrast;}.loader{position: fixed; top: 0; right: 0; bottom: 0; left: 0; display: none; align-items: center; justify-content: center; width: 100%; height: 100%; background: #e7e7e7; pointer-events: none;}.spinner{position: relative; margin: 0 auto; width: 60px; z-index: 999999;}.spinner::before{content: ''; display: block; padding-top: 100%;}.spinner .circular{animation: rotate 1.6s linear infinite; height: 100%; transform-origin: center center; width: 100%; position: absolute; top: 0; bottom: 0; left: 0; right: 0; margin: auto;}.spinner .path{stroke-dasharray: 1, 200; stroke-dashoffset: 0; animation: dash 1.5s ease-in-out infinite; stroke-linecap: round; stroke: #a79fa9;}@keyframes rotate{100%{transform: rotate(360deg);}}@keyframes dash{0%{stroke-dasharray: 1, 200; stroke-dashoffset: 0;}50%{stroke-dasharray: 89, 200; stroke-dashoffset: -35px;}100%{stroke-dasharray: 89, 200; stroke-dashoffset: -124px;}}</style></head><body><div class="image-container" id="image-container"><img src="${convertGSToDownloadURL(
    `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/pages/${projectID}/${currentPage.id}/${versionID}/image`,
  )}" alt="${
    currentPage.title
  }" onload="__ruttl.imageLoadSuccess(event);" onerror="__ruttl.retryImageLoad(event);"></div><div class="loader"> <div class="spinner"> <svg class="circular" viewBox="25 25 50 50"> <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miter-limit="10"/> </svg> </div></div></body></html>`;

export const getGuestUserDetails = (uid) => {
  if (!uid) return {};

  const [match] = uid.match(/_ruttl_guest__[0-9]__/);

  const [, photoID, displayName, email = null] = uid.split('__');

  if (match) {
    return {
      uid,
      displayName,
      photoURL: `avatar-share-${photoID}`,
      email,
    };
  }

  return { uid };
};

// Return `true` if a trailing slash has to be added later.

export const isURLMissingTrailingSlash = (url) => {
  const { href } = new URL(url);

  if (href.endsWith('/')) return false;

  const splits = href.split('/');

  if (!splits[splits.length - 1].includes('.')) return true;

  return false;
};

export const getNodeTitle = (elementType) => {
  switch (elementType.toLowerCase()) {
    case 'p':
      return 'paragraph';
    case 'a':
      return 'link';
    case 'h1':
      return 'heading (h1)';
    case 'h2':
      return 'heading (h2)';
    case 'h3':
      return 'heading (h3)';
    case 'h4':
      return 'heading (h4)';
    case 'h5':
      return 'heading (h5)';
    case 'h6':
      return 'heading (h6)';
    case 'div':
      return 'container';
    case 'span':
    case 'font':
      return 'text';
    case 'img':
      return 'image';
    case 'svg':
      return 'SVG';
    case 'strong':
    case 'b':
      return 'bold text';
    case 'i':
    case 'em':
      return 'italic text';
    case 'u':
    case 'ins':
      return 'underlined text';
    case 'ul':
      return 'unordered list';
    case 'ol':
      return 'ordered list';
    case 'li':
      return 'list item';
    case 'small':
      return 'small text';
    case 'select':
      return 'dropdown';
    case 'pre':
      return 'preformatted text';
    case 'mark':
      return 'highlighted text';
    case 'del':
      return 'strikethrough text';
    case 'sub':
      return 'subscript text';
    case 'sup':
      return 'superscript text';
    default:
      return elementType.toLowerCase();
  }
};

export const getNodeAvatar = (elementType) => {
  const getPath = (type) => `/assets/img/activities/change-type-${type}.svg`;

  switch (elementType.toLowerCase()) {
    case 'a':
    case 'button':
      return getPath('button');
    case 'div':
    case 'body':
    case 'address':
    case 'article':
    case 'aside':
    case 'footer':
    case 'header':
    case 'main':
    case 'nav':
    case 'section':
      return getPath('container');
    case 'img':
    case 'area':
    case 'map':
    case 'svg':
    case 'picture':
    case 'figure':
    case 'iframe':
    case 'embed':
    case 'object':
    case 'math':
    case 'canvas':
      return getPath('image');
    case 'video':
    case 'audio':
    case 'track':
      return getPath('video');
    default:
      return getPath('text');
  }
};

export const getInnerText = (html) => {
  if (typeof html !== 'string') return '';

  const tempEl = document.createElement('template');

  tempEl.innerHTML = html;
  return tempEl.content.textContent;
};

export const getFirebaseAuthError = (code) =>
  debugErrorMap()[code.replace('auth/', '')];

export const scrollToFrameElement = (
  frameInstance,
  commentID,
  isHover = false,
  allowScroll = true,
) => {
  const iframeParent = document.getElementById('iframe-parent');
  const ruttlShadow =
    frameInstance?.contentDocument?.querySelector('ruttl-shadow') || null;

  if (ruttlShadow) {
    const dot = ruttlShadow.shadowRoot.querySelector(
      `#commentor-dots [data-dot-id="${commentID}"]`,
    );

    if (dot) {
      if (isHover) dot.classList.add('selected');

      if (frameInstance.contentWindow && allowScroll) {
        frameInstance.contentWindow.scrollTo({
          top: pxToNumber(dot.style.top) - 200,
          behavior: 'smooth',
        });
        if (iframeParent)
          iframeParent.scrollTo({
            left:
              pxToNumber(dot.getBoundingClientRect().left) +
                iframeParent.scrollLeft >
              window.innerWidth - 330
                ? iframeParent.offsetWidth
                : 0,
            behavior: 'smooth',
          });
      }

      return dot;
    }
  }

  return null;
};

const getChangesWithUniqueStyle = (changes = []) => {
  let styleChanges = changes.filter(({ changeType }) => changeType === 'style');

  if (styleChanges?.length > 0) {
    styleChanges = Array.from(
      new Set(styleChanges.map(({ styleProperty }) => styleProperty)),
    );

    return changes
      .map((change) => {
        if (change.changeType === 'style') {
          if (styleChanges.includes(change.styleProperty)) {
            styleChanges = styleChanges.filter(
              (i) => i !== change.styleProperty,
            );
            return change;
          }

          return null;
        }

        return change;
      })
      .filter((i) => Boolean(i));
  }

  return changes;
};

export const getInspectChangesByElement = (
  inspectChanges,
  frameInstance,
  showUnresolvedOnly = false,
) => {
  if (typeof frameInstance === 'undefined') return [];

  const elements = Array.from(new Set(inspectChanges.map(({ id }) => id)));

  if (elements.length > 0 && frameInstance?.contentDocument) {
    const newElements = elements
      .map((id) => {
        const thisChange = inspectChanges.find((i) => i.id === id);
        const thisElement = frameInstance.contentDocument.querySelector(
          thisChange.querySelector
            ? thisChange.querySelector
            : `[data-rally-element-id="${thisChange.id}"]`,
        );
        let changes = inspectChanges.filter((c) => c.id === id);

        if (!thisElement) return null;

        if (changes.length === 0) return null;

        changes = changes.sort((a, b) => b.createdAt - a.createdAt);

        if (
          changes.filter(({ changeType }) => changeType === 'text').length > 0
        ) {
          changes = [
            changes.filter(({ changeType }) => changeType === 'text')[0],
            ...changes.filter(({ changeType }) => changeType !== 'text'),
          ];
        }

        changes = getChangesWithUniqueStyle(changes);

        return {
          id,
          querySelector: thisChange.querySelector,
          el: thisElement,
          uniqueUsers: Array.from(
            new Set(
              inspectChanges.map((i) => i.userID).filter((i) => Boolean(i)),
            ),
          ),
          changes,
        };
      })
      .filter((i) => i)
      .sort((a, b) => b.changes[0].createdAt - a.changes[0].createdAt);

    return newElements
      .map((c, index) => ({
        ...c,
        index: newElements.length - index,
      }))
      .filter((e) =>
        showUnresolvedOnly === true
          ? e.changes.some((c) => !c.resolvedBy)
          : true,
      );
  }

  return [];
};

export const uploadURLToStorage = async (fileURL, storageReference) => {
  const downloadedFile = await axios.get(`/proxy/?url=${fileURL}`, {
    responseType: 'blob',
  });

  if (downloadedFile.status === 200)
    await uploadBytes(ref(storage, storageReference), downloadedFile.data, {
      cacheControl: 'private,max-age=31536000',
    });
};

export const getMimeTypeFromDateURI = (dataURI) =>
  dataURI.split(',')[0].split(':')[1].split(';')[0];

export const dataURItoBlob = (dataURI) => {
  const byteString = atob(dataURI.split(',')[1]);

  const mimeString = getMimeTypeFromDateURI(dataURI);

  const ab = new ArrayBuffer(byteString.length);

  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([ab], { type: mimeString });

  return blob;
};

export const fixedChangeValue = (change) => {
  switch (change.styleProperty) {
    case 'opacity':
      return Number(change.styleValue).toPrecision(2);

    case 'border-radius':
      return `${pxToNumber(change.styleValue).toPrecision(2)}px`;

    default:
      return change.styleValue;
  }
};

export const isEditChangeResolved = (changes) =>
  changes.filter(({ resolvedBy }) => Boolean(resolvedBy)).length ===
  changes.length;

/* eslint-disable indent */
export const whoResolvedEdit =
  (users = []) =>
  (changes = []) => {
    const match = users.find(({ uid }) =>
      changes.some(({ resolvedBy }) => resolvedBy === uid),
    );

    return match ? match.displayName : 'Deleted User';
  };
/* eslint-enable indent */

export const formatBytes = (bytes = 0, decimals = 2) => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // eslint-disable-next-line no-restricted-properties
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const getFileNameWithoutExt = (file) => {
  if (!file || !file.name) return '';

  return file.name.substring(0, file.name.lastIndexOf('.'));
};

/**
 * Checks for active appsumo plan.
 * @param {object} billing `billing` object from store.
 * @returns {boolean|string|null} `null` in the case if `billing` is `null`.
 */

export const getSumoTier = (billing) => {
  if (billing === null) return null;

  if (
    billing &&
    typeof billing.sumoLing === 'object' &&
    billing.sumoLing.isDisabled === false
  )
    return appsSumoTiers.find(
      ({ id }) => id === getAppsumoLastInvoice(billing.sumoLing.invoices).plan,
    );

  return false;
};

/**
 * Returns the element on which the comment was made.
 * @param {string} id
 * @param {array} comments
 * @param {object} frameInstance
 * @returns {HTMLElement}
 */

export const findCommentElementById = (id, comments, frameInstance) => {
  const currentComment = comments.find((c) => c.id === id);

  if (currentComment && typeof currentComment.querySelector === 'string')
    return frameInstance.contentDocument.querySelector(
      currentComment.querySelector,
    );

  return frameInstance.contentDocument.querySelector(
    `[data-rally-element-id="${id}"]`,
  );
};
